/**
 * Jyotish Tarang — Admin Panel v2.6
 * Stats | Users (+ CSV export) | Payments | Discounts | Activity Log | Audit | Health
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

type AdminTab = 'stats' | 'users' | 'audit' | 'payments' | 'health' | 'discounts' | 'activitylog' | 'featureusage' | 'tierconfig';

export default function AdminPage() {
  const [tab,    setTab]    = useState<AdminTab>('stats');
  const [stats,  setStats]  = useState<any>(null);
  const [users,  setUsers]  = useState<any[]>([]);
  const [audit,  setAudit]  = useState<any[]>([]);
  const [payments, setPay]  = useState<any[]>([]);
  const [health, setHealth] = useState<any>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [userPage,   setUserPage]  = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [selUser, setSelUser] = useState<any>(null);
  const [totp,   setTotp]   = useState('');
  const [totpOk, setTotpOk] = useState(false);
  const [totpErr,setTotpErr]= useState('');
  const [loading,setLoading]= useState(false);
  // Tier config
  const [tierConfig, setTierConfig] = useState<any[]>([]);
  const [tierConfigEdits, setTierConfigEdits] = useState<Record<string,string>>({});
  const [tierConfigMsg, setTierConfigMsg] = useState('');
  const [tierConfigSaving, setTierConfigSaving] = useState(false);
  const [tierConfigAudit, setTierConfigAudit] = useState<any[]>([]);
  // Feature usage
  const [featureData, setFeatureData] = useState<any>(null);
  const [featureDays, setFeatureDays] = useState(30);
  const [featureView, setFeatureView] = useState<'table'|'chart'>('table');
  const [drilldown, setDrilldown] = useState<any>(null);
  const [drillLoading, setDrillLoading] = useState(false);
  // Discounts
  const [discounts, setDiscounts] = useState<any[]>([]);
  const [showNewDiscount, setShowNewDiscount] = useState(false);
  const [newDiscount, setNewDiscount] = useState({code:'',discount_pct:'',valid_from:'',valid_to:'',max_uses:'',allowed_emails:''});
  const [discountMsg, setDiscountMsg] = useState('');
  // Activity log
  const [activityLog, setActivityLog] = useState<any[]>([]);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityFilter, setActivityFilter] = useState({ action:'', status:'', q:'' });

  async function loadTierConfig() {
    try {
      const r = await axios.get('/admin/tier-config');
      setTierConfig(r.data || []);
      // Pre-fill edits with current values
      const edits: Record<string,string> = {};
      for (const item of (r.data || [])) {
        if (item.value_type === 'json') {
          try { edits[item.key] = Array.isArray(item.value_parsed)
            ? item.value_parsed.join('\n') : item.value; }
          catch { edits[item.key] = item.value; }
        } else {
          edits[item.key] = String(item.value_parsed ?? item.value);
        }
      }
      setTierConfigEdits(edits);
      // Load audit
      const ra = await axios.get('/admin/tier-config/audit');
      setTierConfigAudit(ra.data || []);
    } catch {}
  }

  async function saveTierConfig() {
    setTierConfigSaving(true); setTierConfigMsg('');
    try {
      // Build updates object
      const updates: Record<string,any> = {};
      for (const item of tierConfig) {
        const edited = tierConfigEdits[item.key];
        if (edited === undefined) continue;
        if (item.value_type === 'json') {
          // Convert newline-separated text to array
          updates[item.key] = edited.split('\n').map((s:string)=>s.trim()).filter(Boolean);
        } else {
          updates[item.key] = edited;
        }
      }
      const r = await axios.post('/admin/tier-config', { updates });
      setTierConfigMsg(`✓ Saved: ${r.data.saved?.join(', ')}`);
      loadTierConfig();
    } catch (e: any) {
      setTierConfigMsg('✗ ' + (e.response?.data?.error || 'Save failed'));
    } finally { setTierConfigSaving(false); }
  }

  async function verifyTotp() {
    if (!totp || totp.length !== 6) { setTotpErr('Enter 6-digit code'); return; }
    try {
      // Use /admin/totp-verify which issues a 60-min DB session token
      const r = await axios.post('/admin/totp-verify', { code: totp });
      const sessionToken = r.data.session_token;
      // Store session token in sessionStorage for page navigation persistence
      sessionStorage.setItem('admin_session_token', sessionToken);
      // Set as default header for all subsequent axios calls
      axios.defaults.headers.common['X-Admin-Session'] = sessionToken;
      axios.defaults.headers.common['X-Admin-Verified'] = 'true';
      setTotpOk(true);
      loadStats();
    } catch { setTotpErr('Invalid TOTP code. Check Google Authenticator.'); }
  }

  async function loadFeatureUsage(days = featureDays) {
    try {
      const r = await axios.get(`/admin/feature-usage?days=${days}`);
      setFeatureData(r.data);
    } catch {}
  }
  async function loadDrilldown(action: string, tier: string, days: number) {
    setDrillLoading(true);
    try {
      const r = await axios.get(`/admin/feature-usage/drilldown?action=${action}&tier=${tier}&days=${days}`);
      setDrilldown(r.data);
    } catch {} finally { setDrillLoading(false); }
  }
  async function loadStats() {
    try { const r = await axios.get('/admin/stats'); setStats(r.data); } catch {}
  }
  async function loadUsers(q = '', page = 1) {
    const r = await axios.get(`/admin/users?search=${encodeURIComponent(q)}&limit=50&page=${page}`);
    setUsers(r.data.users || []);
    setUsersTotal(r.data.total || 0);
    setUserPage(page);
  }
  async function loadAudit() {
    const r = await axios.get('/admin/audit'); setAudit(r.data || []);
  }
  async function loadPayments() {
    const r = await axios.get('/admin/payments'); setPay(r.data || []);
  }
  async function loadHealth(refresh=false) {
    setHealthLoading(true);
    try {
      const r = await axios.get(`/admin/health${refresh?'?refresh=1':''}`);
      setHealth(r.data);
    } catch {}
    finally { setHealthLoading(false); }
  }
  async function loadUserDetail(uid: number) {
    const r = await axios.get(`/admin/users/${uid}`); setSelUser(r.data);
  }
  async function loadDiscounts() {
    try { const r = await axios.get('/admin/discounts'); setDiscounts(r.data || []); } catch {}
  }
  async function loadActivityLog(page = 1) {
    try {
      const { action, status, q } = activityFilter;
      const params: Record<string, string> = { page: String(page), per_page: '50' };
      if (action) params.action = action;
      if (status) params.status = status;
      if (q) params.q = q;
      const r = await axios.get('/admin/activity-log', { params });
      setActivityLog(r.data.rows || []);
      setActivityTotal(r.data.total || 0);
      setActivityPage(page);
    } catch {}
  }
  async function handleCreateDiscount() {
    setDiscountMsg('');
    try {
      await axios.post('/admin/discounts', {
        code: newDiscount.code.trim().toUpperCase(),
        discount_pct: parseFloat(newDiscount.discount_pct),
        valid_from: newDiscount.valid_from || null,
        valid_to: newDiscount.valid_to || null,
        max_uses: newDiscount.max_uses ? parseInt(newDiscount.max_uses) : null,
        allowed_emails: newDiscount.allowed_emails || '',
      });
      setDiscountMsg('✓ Code created');
      setShowNewDiscount(false);
      setNewDiscount({code:'',discount_pct:'',valid_from:'',valid_to:'',max_uses:'',allowed_emails:''});
      loadDiscounts();
    } catch (e: any) {
      setDiscountMsg('✗ ' + (e.response?.data?.error || 'Error creating code'));
    }
  }
  async function handleToggleDiscount(id: number) {
    await axios.post(`/admin/discounts/${id}/toggle`);
    loadDiscounts();
  }

  // Check for existing admin session on mount
  useEffect(() => {
    const savedSession = sessionStorage.getItem('admin_session_token');
    if (savedSession) {
      // Validate session is still alive by attempting a lightweight admin call
      axios.get('/admin/stats', {
        headers: {
          'X-Admin-Session': savedSession,
          'X-Admin-Verified': 'true',
        }
      }).then(() => {
        // Session valid — set axios defaults and skip TOTP
        axios.defaults.headers.common['X-Admin-Session'] = savedSession;
        axios.defaults.headers.common['X-Admin-Verified'] = 'true';
        setTotpOk(true);
      }).catch(() => {
        // Session expired — clear it
        sessionStorage.removeItem('admin_session_token');
        delete axios.defaults.headers.common['X-Admin-Session'];
      });
    }
  }, []);

  useEffect(() => {
    if (!totpOk) return;
    if (tab === 'stats')       loadStats();
    if (tab === 'users')       loadUsers();
    if (tab === 'audit')       loadAudit();
    if (tab === 'payments')    loadPayments();
    if (tab === 'health')      loadHealth();
    if (tab === 'discounts')   loadDiscounts();
    if (tab === 'activitylog') loadActivityLog(1);
    if (tab === 'featureusage') loadFeatureUsage(featureDays);
    if (tab === 'tierconfig') loadTierConfig();
  }, [tab, totpOk]);

  if (!totpOk) return (
    <div className="page-wrap">
      <div className="container-sm">
        <div className="card" style={{ maxWidth:380, margin:'0 auto', textAlign:'center', padding:32 }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🔐</div>
          <h2 style={{ fontFamily:'var(--font-display)', color:'var(--indigo)', marginBottom:8 }}>Admin Access</h2>
          <p style={{ fontSize:13, color:'var(--ink3)', marginBottom:20 }}>Enter your Google Authenticator code.</p>
          {totpErr && <div className="info-box info-red" style={{ marginBottom:12 }}>{totpErr}</div>}
          <input className="form-input otp-input"
                 value={totp} onChange={e => setTotp(e.target.value.replace(/\D/g,'').slice(0,6))}
                 placeholder="● ● ● ● ● ●" maxLength={6}
                 onKeyDown={e => e.key==='Enter' && verifyTotp()} autoFocus/>
          <button className="btn btn-primary btn-full" style={{ marginTop:12 }}
                  onClick={verifyTotp} disabled={totp.length !== 6}>Verify →</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="page-wrap">
      <div className="container">
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
          <h2 style={{ fontFamily:'var(--font-display)', color:'var(--indigo)' }}>Admin Panel</h2>
          <span className="badge badge-red">ADMIN</span>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:4, marginBottom:20, flexWrap:'wrap' }}>
          {([
            ['stats','📊 Stats'],['featureusage','📈 Feature Usage'],['tierconfig','⚙ Tier Config'],['users','👥 Users'],['payments','💳 Payments'],
            ['discounts','🏷 Discounts'],['activitylog','📋 Activity Log'],
            ['audit','🔍 Audit'],['health','💚 Health'],
          ] as [AdminTab, string][]).map(([id, label]) => (
            <button key={id} className={`btn btn-sm ${tab===id?'btn-primary':'btn-ghost'}`}
                    onClick={() => { setTab(id); setSelUser(null); }}>
              {label}
            </button>
          ))}
        </div>


        {/* ── Feature Usage ── */}
        {tab === 'featureusage' && (
          <div>
            {/* Controls */}
            <div style={{ display:'flex', gap:8, marginBottom:16, alignItems:'center', flexWrap:'wrap' }}>
              <h3 style={{ fontSize:14, color:'var(--indigo)', marginRight:8 }}>Feature Usage</h3>
              {featureData?.last_snapshot && (
                <span style={{ fontSize:11, color:'var(--ink3)' }}>
                  Last updated: {featureData.last_snapshot} (daily at 00:00 IST)
                </span>
              )}
              <div style={{ display:'flex', gap:4, marginLeft:'auto' }}>
                {(['table','chart'] as const).map(v => (
                  <button key={v} className={`btn btn-sm ${featureView===v?'btn-primary':'btn-ghost'}`}
                          onClick={() => setFeatureView(v)}>
                    {v === 'table' ? '⊞ Table' : '▦ Chart'}
                  </button>
                ))}
              </div>
              <select className="form-input" style={{ maxWidth:130, padding:'4px 8px', fontSize:12 }}
                      value={featureDays}
                      onChange={e => { const d=parseInt(e.target.value); setFeatureDays(d); loadFeatureUsage(d); setDrilldown(null); }}>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={0}>All time</option>
              </select>
            </div>

            {!featureData ? (
              <div style={{ padding:40, textAlign:'center', color:'var(--ink3)' }}>Loading...</div>
            ) : featureView === 'table' ? (
              /* ── Table view ── */
              <div>
                <div className="card" style={{ padding:0, overflow:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                    <thead>
                      <tr style={{ background:'var(--indigo)', color:'#fff' }}>
                        <th style={{ padding:'8px 12px', textAlign:'left', fontSize:11 }}>Feature</th>
                        {['free','pro','expert'].map(tier => (
                          <th key={tier} colSpan={2} style={{
                            padding:'8px 12px', textAlign:'center', fontSize:11,
                            background: tier==='free'?'#b45309':tier==='pro'?'#1565c0':'#6a1b9a'
                          }}>
                            {tier==='free'?'Free':tier==='pro'?'Gold':'Platinum'}
                          </th>
                        ))}
                      </tr>
                      <tr style={{ background:'var(--indigo)', color:'rgba(255,255,255,0.7)' }}>
                        <th style={{ padding:'4px 12px', fontSize:10 }}></th>
                        {['free','pro','expert'].flatMap(tier => [
                          <th key={tier+'u'} style={{ padding:'4px 8px', textAlign:'center', fontSize:10, fontWeight:400 }}>Users</th>,
                          <th key={tier+'t'} style={{ padding:'4px 8px', textAlign:'center', fontSize:10, fontWeight:400 }}>Uses</th>
                        ])}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(featureData.actions || {}).map(([action, tiers]: [string, any], i) => (
                        <tr key={action} style={{ background:i%2===0?'var(--cream)':'var(--ivory)', borderBottom:'0.5px solid var(--parch2)' }}>
                          <td style={{ padding:'8px 12px', fontWeight:500, color:'var(--indigo)', whiteSpace:'nowrap' }}>
                            {action.replace(/_/g,' ')}
                          </td>
                          {['free','pro','expert'].flatMap(tier => {
                            const d = tiers[tier];
                            return [
                              <td key={tier+'u'} style={{ padding:'8px', textAlign:'center' }}>
                                {d ? (
                                  <span style={{ color:'var(--sf)', cursor:'pointer', textDecoration:'underline', fontWeight:500 }}
                                        onClick={() => { loadDrilldown(action, tier, featureDays); }}>
                                    {d.users}
                                  </span>
                                ) : <span style={{ color:'var(--parch2)' }}>—</span>}
                              </td>,
                              <td key={tier+'t'} style={{ padding:'8px', textAlign:'center' }}>
                                {d ? (
                                  <span style={{ color:'var(--indigo)', cursor:'pointer', textDecoration:'underline', fontWeight:500 }}
                                        onClick={() => { loadDrilldown(action, tier, featureDays); }}>
                                    {d.uses}
                                  </span>
                                ) : <span style={{ color:'var(--parch2)' }}>—</span>}
                              </td>
                            ];
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              /* ── Chart view ── */
              <div className="card" style={{ padding:16 }}>
                <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
                  {['free','pro','expert'].map((tier, ti) => (
                    <span key={tier} style={{ fontSize:11, display:'flex', alignItems:'center', gap:4 }}>
                      <span style={{ width:10, height:10, borderRadius:2, display:'inline-block',
                                     background:ti===0?'#b45309':ti===1?'#1565c0':'#6a1b9a' }}></span>
                      {tier==='free'?'Free':tier==='pro'?'Gold':'Platinum'}
                    </span>
                  ))}
                </div>
                <div style={{ display:'flex', gap:16, alignItems:'flex-end', overflowX:'auto', paddingBottom:8 }}>
                  {Object.entries(featureData.actions || {}).map(([action, tiers]: [string, any]) => {
                    const maxUses = Math.max(...Object.values(featureData.actions).flatMap(
                      (t: any) => Object.values(t).map((v: any) => v.uses)
                    ));
                    return (
                      <div key={action} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, minWidth:80 }}>
                        <div style={{ display:'flex', gap:3, alignItems:'flex-end', height:120 }}>
                          {['free','pro','expert'].map((tier, ti) => {
                            const d = (tiers as any)[tier];
                            const h = d ? Math.max(4, Math.round((d.uses / maxUses) * 110)) : 0;
                            const color = ti===0?'#b45309':ti===1?'#1565c0':'#6a1b9a';
                            return (
                              <div key={tier} title={d?`${tier}: ${d.uses} uses`:'No data'}
                                   onClick={() => d && loadDrilldown(action, tier, featureDays)}
                                   style={{ width:16, height:h||2, background:d?color:'var(--parch2)',
                                            borderRadius:'2px 2px 0 0', cursor:d?'pointer':'default',
                                            transition:'opacity 0.2s' }}>
                              </div>
                            );
                          })}
                        </div>
                        <span style={{ fontSize:9, color:'var(--ink3)', textAlign:'center', maxWidth:80,
                                       overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {action.replace(/_/g,' ')}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize:11, color:'var(--ink3)', marginTop:8 }}>Click any bar to drill down</div>
              </div>
            )}

            {/* ── Drilldown panel ── */}
            {drilldown && (
              <div className="card" style={{ marginTop:16, border:'1.5px solid var(--sfb)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                  <h4 style={{ fontSize:13, color:'var(--indigo)' }}>
                    {drilldown.action.replace(/_/g,' ')} ·{' '}
                    {drilldown.tier==='free'?'Free':drilldown.tier==='pro'?'Gold':'Platinum'} ·{' '}
                    {drilldown.days===0?'All time':`Last ${drilldown.days} days`}
                  </h4>
                  <button className="btn btn-ghost btn-sm" onClick={() => setDrilldown(null)}>✕ Close</button>
                </div>
                {drillLoading ? (
                  <div style={{ padding:20, textAlign:'center', color:'var(--ink3)' }}>Loading...</div>
                ) : (
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                    <thead>
                      <tr style={{ background:'var(--indigo)', color:'#fff' }}>
                        <th style={{ padding:'7px 12px', textAlign:'left', fontSize:11 }}>Email</th>
                        <th style={{ padding:'7px 12px', textAlign:'left', fontSize:11 }}>Name</th>
                        <th style={{ padding:'7px 12px', textAlign:'right', fontSize:11 }}>Uses</th>
                        <th style={{ padding:'7px 12px', textAlign:'right', fontSize:11 }}>Last Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drilldown.users.map((u: any, i: number) => (
                        <tr key={u.email} style={{ background:i%2===0?'var(--cream)':'var(--ivory)', borderBottom:'0.5px solid var(--parch2)' }}>
                          <td style={{ padding:'7px 12px' }}>{u.email}</td>
                          <td style={{ padding:'7px 12px', color:'var(--ink3)' }}>{u.name||'—'}</td>
                          <td style={{ padding:'7px 12px', textAlign:'right', fontWeight:600, color:'var(--indigo)', fontFamily:'var(--font-mono)' }}>{u.uses}</td>
                          <td style={{ padding:'7px 12px', textAlign:'right', color:'var(--ink3)' }}>{u.last_used.slice(0,10)}</td>
                        </tr>
                      ))}
                      {drilldown.users.length === 0 && (
                        <tr><td colSpan={4} style={{ padding:16, textAlign:'center', color:'var(--ink3)' }}>No data for this period</td></tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ── Recommendations ── */}
            {featureData && (() => {
              const actions = featureData.actions || {};
              const recs: {color: string, title: string, desc: string}[] = [];
              // Most used free feature
              const freeActions = Object.entries(actions)
                .filter(([,t]: [string,any]) => t.free)
                .sort(([,a]: [string,any],[,b]: [string,any]) => b.free.uses - a.free.uses);
              if (freeActions.length > 0) {
                const [topAction, topTiers]: [string, any] = freeActions[0] as any;
                recs.push({
                  color: '#e07000',
                  title: `🔥 Most used by free users: ${topAction.replace(/_/g,' ')} (${topTiers.free.uses} uses, ${topTiers.free.users} users)`,
                  desc: `Consider limiting free tier to drive Gold upgrades. Active free users hitting a limit are most likely to convert.`
                });
              }
              // Paid-only features with zero free usage
              const paidOnly = Object.entries(actions)
                .filter(([,t]: [string,any]) => (t.expert || t.pro) && !t.free);
              if (paidOnly.length > 0) {
                recs.push({
                  color: '#1565c0',
                  title: `👁 ${paidOnly.length} feature(s) with zero free usage: ${paidOnly.map(([a]) => a.replace(/_/g,' ')).join(', ')}`,
                  desc: `Free users haven't tried these paid features. A locked preview or "teaser" could drive Platinum upgrades.`
                });
              }
              // Missing Gold tier
              const hasGold = Object.values(actions).some((t: any) => t.pro);
              if (!hasGold) {
                recs.push({
                  color: '#2e7d32',
                  title: `⚠ No Gold (Pro) tier usage detected`,
                  desc: `All paid usage is Platinum. Gold tier may need better positioning, a lower price point, or a trial offer to attract first-time paid users.`
                });
              }
              return recs.length > 0 ? (
                <div style={{ marginTop:20 }}>
                  <div style={{ fontSize:12, fontWeight:500, color:'var(--indigo)', marginBottom:10 }}>💡 Monetisation Recommendations</div>
                  {recs.map((rec, i) => (
                    <div key={i} style={{ padding:'12px 14px', marginBottom:8, background:'var(--cream)',
                                          borderLeft:`3px solid ${rec.color}`, borderRadius:4 }}>
                      <div style={{ fontSize:13, fontWeight:500, marginBottom:4 }}>{rec.title}</div>
                      <div style={{ fontSize:12, color:'var(--ink2)', lineHeight:1.5 }}>{rec.desc}</div>
                    </div>
                  ))}
                  <div style={{ fontSize:11, color:'var(--ink3)', marginTop:8, padding:'8px 12px',
                                background:'var(--parch)', borderRadius:4 }}>
                    ⏱ Activity log started 26 May 2026. Recommendations strengthen after 30+ days of data.
                    {featureData.last_snapshot && ` Last snapshot: ${featureData.last_snapshot}.`}
                  </div>
                </div>
              ) : null;
            })()}
          </div>
        )}


        {/* ── Tier Config ── */}
        {tab === 'tierconfig' && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <div>
                <h3 style={{ fontSize:14, color:'var(--indigo)', marginBottom:4 }}>Tier Configuration</h3>
                <p style={{ fontSize:12, color:'var(--ink3)' }}>Changes apply immediately. All changes are audit logged.</p>
              </div>
              <button className="btn btn-primary"
                      disabled={tierConfigSaving}
                      onClick={saveTierConfig}>
                {tierConfigSaving ? 'Saving...' : 'Save All Changes'}
              </button>
            </div>
            {tierConfigMsg && (
              <div className={`info-box ${tierConfigMsg.startsWith('✓') ? 'info-teal' : 'info-red'}`}
                   style={{ marginBottom:16 }}>{tierConfigMsg}</div>
            )}
            {tierConfig.length === 0 ? (
              <div style={{ padding:40, textAlign:'center', color:'var(--ink3)' }}>Loading config...</div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                {/* Left column — numeric limits */}
                <div>
                  {[
                    { section: 'AI Question Limits', keys: ['free_ai_lifetime','gold_ai_monthly','platinum_ai_monthly'] },
                    { section: 'Chart Limits', keys: ['free_charts_lifetime','gold_charts_total','platinum_charts_monthly'] },
                    { section: 'PDF Export Limits (per day)', keys: ['gold_pdf_daily','platinum_pdf_daily'] },
                  ].map(({ section, keys }) => (
                    <div key={section} className="card" style={{ marginBottom:16 }}>
                      <h4 style={{ fontSize:12, color:'var(--indigo)', marginBottom:12, textTransform:'uppercase', letterSpacing:0.5 }}>{section}</h4>
                      {tierConfig.filter(c => keys.includes(c.key)).map(c => (
                        <div key={c.key} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
                          <label style={{ fontSize:12, color:'var(--ink2)', flex:1 }}>{c.label}</label>
                          <input className="form-input" type="number" min="0"
                                 style={{ width:80, fontFamily:'var(--font-mono)' }}
                                 value={tierConfigEdits[c.key] ?? String(c.value_parsed)}
                                 onChange={e => setTierConfigEdits({...tierConfigEdits, [c.key]: e.target.value})}/>
                        </div>
                      ))}
                    </div>
                  ))}
                  {/* Prices */}
                  <div className="card" style={{ marginBottom:16 }}>
                    <h4 style={{ fontSize:12, color:'var(--indigo)', marginBottom:12, textTransform:'uppercase', letterSpacing:0.5 }}>Prices (in paise — ₹1 = 100 paise)</h4>
                    {tierConfig.filter(c => c.key.includes('price')).map(c => (
                      <div key={c.key} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
                        <label style={{ fontSize:12, color:'var(--ink2)', flex:1 }}>
                          {c.label}
                          <span style={{ color:'var(--teal)', marginLeft:6, fontSize:11 }}>
                            = ₹{Math.round(parseInt(tierConfigEdits[c.key] || '0') / 100)}
                          </span>
                        </label>
                        <input className="form-input" type="number" min="100"
                               style={{ width:100, fontFamily:'var(--font-mono)' }}
                               value={tierConfigEdits[c.key] ?? String(c.value_parsed)}
                               onChange={e => setTierConfigEdits({...tierConfigEdits, [c.key]: e.target.value})}/>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Right column — feature lists */}
                <div>
                  {['gold_features','platinum_features'].map(key => {
                    const c = tierConfig.find(x => x.key === key);
                    if (!c) return null;
                    return (
                      <div key={key} className="card" style={{ marginBottom:16 }}>
                        <h4 style={{ fontSize:12, color:'var(--indigo)', marginBottom:8, textTransform:'uppercase', letterSpacing:0.5 }}>
                          {key === 'gold_features' ? 'Gold Features' : 'Platinum Features'}
                        </h4>
                        <p style={{ fontSize:11, color:'var(--ink3)', marginBottom:8 }}>One feature per line. Changes reflect on Pricing page immediately.</p>
                        <textarea className="form-input"
                                  style={{ width:'100%', height:200, resize:'vertical', fontSize:12, lineHeight:1.6 }}
                                  value={tierConfigEdits[key] ?? ''}
                                  onChange={e => setTierConfigEdits({...tierConfigEdits, [key]: e.target.value})}/>
                        <p style={{ fontSize:11, color:'var(--ink3)', marginTop:4 }}>
                          {(tierConfigEdits[key] || '').split('\n').filter(Boolean).length} features
                        </p>
                      </div>
                    );
                  })}
                  {/* Audit trail */}
                  <div className="card">
                    <h4 style={{ fontSize:12, color:'var(--indigo)', marginBottom:10, textTransform:'uppercase', letterSpacing:0.5 }}>Recent Changes</h4>
                    {tierConfigAudit.slice(0,10).map((a,i) => (
                      <div key={i} style={{ padding:'6px 0', borderBottom:'0.5px solid var(--parch2)', fontSize:11 }}>
                        <div style={{ display:'flex', justifyContent:'space-between' }}>
                          <span style={{ fontFamily:'var(--font-mono)', color:'var(--sf)' }}>{a.key}</span>
                          <span style={{ color:'var(--ink3)' }}>{String(a.changed_at).slice(0,16)}</span>
                        </div>
                        <div style={{ color:'var(--ink3)', marginTop:2 }}>
                          <s style={{ marginRight:6 }}>{String(a.old_value).slice(0,40)}</s>
                          → <span style={{ color:'var(--teal)' }}>{String(a.new_value).slice(0,40)}</span>
                        </div>
                        <div style={{ color:'var(--ink3)', fontSize:10 }}>by {a.changed_by}</div>
                      </div>
                    ))}
                    {tierConfigAudit.length === 0 && (
                      <p style={{ fontSize:12, color:'var(--ink3)' }}>No changes yet.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Stats ── */}
        {tab === 'stats' && stats && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
            {[
              { label:'Total users',   val: stats.users.total },
              { label:'New today',     val: stats.users.today },
              { label:'Free tier',     val: stats.users.free },
              { label:'Pro tier',      val: stats.users.pro },
              { label:'Expert tier',   val: stats.users.expert },
              { label:'Total charts',  val: stats.charts.total },
              { label:'Charts today',  val: stats.charts.today },
              { label:'AI today',      val: stats.ai_questions.today },
              { label:'AI this month', val: stats.ai_questions.this_month },
              { label:'PDFs today',    val: stats.pdfs.today },
              { label:'Active Pro',    val: stats.revenue.active_pro },
              { label:'Active Expert', val: stats.revenue.active_expert },
            ].map(({ label, val }) => (
              <div key={label} style={{ padding:'16px 20px', background:'var(--cream)', borderRadius:10, border:'0.5px solid var(--parch2)' }}>
                <div style={{ fontSize:11, color:'var(--ink3)', textTransform:'uppercase', letterSpacing:0.5 }}>{label}</div>
                <div style={{ fontSize:28, fontFamily:'var(--font-mono)', fontWeight:600, color:'var(--indigo)' }}>{val}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Users ── */}
        {tab === 'users' && !selUser && (
          <div>
            <div style={{ display:'flex', gap:10, marginBottom:12 }}>
              <input className="form-input" style={{ maxWidth:340 }}
                     value={search} onChange={e => setSearch(e.target.value)}
                     placeholder="Search by email or name..."
                     onKeyDown={e => e.key==='Enter' && loadUsers(search)}/>
              <button className="btn btn-primary btn-sm" onClick={() => loadUsers(search)}>Search</button>
              <button className="btn btn-ghost btn-sm" style={{ marginLeft:'auto' }}
                      onClick={async () => {
                        try {
                          const resp = await axios.get('/admin/users/export', { responseType: 'blob' });
                          const url  = URL.createObjectURL(resp.data);
                          const a    = document.createElement('a');
                          a.href     = url;
                          a.download = `jyotish_users_${new Date().toISOString().slice(0,10)}.csv`;
                          a.click();
                          URL.revokeObjectURL(url);
                        } catch { alert('Export failed. Please try again.'); }
                      }}>
                ↓ Download CSV
              </button>
            </div>
            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ background:'var(--indigo)', color:'#fff' }}>
                    {['ID','Email','Name','Role','Tier','Login','Status','Joined','Action'].map(h => (
                      <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:11, fontWeight:500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => (
                    <tr key={u.id} style={{ background:i%2===0?'var(--cream)':'var(--ivory)', borderBottom:'0.5px solid var(--parch2)' }}>
                      <td style={{ padding:'7px 10px', color:'var(--ink3)' }}>{u.id}</td>
                      <td style={{ padding:'7px 10px', color:'var(--ink)' }}>{u.email}</td>
                      <td style={{ padding:'7px 10px', color:'var(--ink)' }}>{u.name}</td>
                      <td style={{ padding:'7px 10px' }}>{u.role==='admin' && <span className="badge badge-red">ADMIN</span>}</td>
                      <td style={{ padding:'7px 10px' }}>
                        <span className={`badge ${u.tier==='expert'?'badge-gold':u.tier==='pro'?'badge-saffron':'badge-indigo'}`}>{u.tier}</span>
                      </td>
                      <td style={{ padding:'7px 10px', color:'var(--ink3)' }}>{u.login_method}</td>
                      <td style={{ padding:'7px 10px' }}>
                        {u.is_active ? <span style={{ color:'var(--teal)', fontSize:11 }}>Active</span>
                                     : <span style={{ color:'var(--red)', fontSize:11 }}>Suspended</span>}
                      </td>
                      <td style={{ padding:'7px 10px', color:'var(--ink3)' }}>{String(u.created_at).slice(0,19)}</td>
                      <td style={{ padding:'7px 10px' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => loadUserDetail(u.id)}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {usersTotal > 50 && (
              <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:12 }}>
                {userPage > 1 && (
                  <button className="btn btn-ghost btn-sm" onClick={() => loadUsers(search, userPage-1)}>← Prev</button>
                )}
                <span style={{ fontSize:12, color:'var(--ink3)', alignSelf:'center' }}>
                  Page {userPage} of {Math.ceil(usersTotal/50)} ({usersTotal} users)
                </span>
                {userPage < Math.ceil(usersTotal/50) && (
                  <button className="btn btn-ghost btn-sm" onClick={() => loadUsers(search, userPage+1)}>Next →</button>
                )}
              </div>
            )}
          </div>
        )}

        {/* User detail */}
        {tab === 'users' && selUser && (
          <div>
            <button className="btn btn-ghost btn-sm" style={{ marginBottom:16 }} onClick={() => setSelUser(null)}>← Back to users</button>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              <div className="card">
                <h3 style={{ fontSize:13, color:'var(--indigo)', marginBottom:10 }}>User — {selUser.user?.email}</h3>
                {Object.entries(selUser.user || {}).map(([k, v]) => (
                  <div key={k} style={{ display:'flex', gap:12, marginBottom:4, fontSize:12, borderBottom:'0.5px solid var(--parch2)', paddingBottom:4 }}>
                    <span style={{ color:'var(--ink3)', minWidth:120 }}>{k}</span>
                    <span style={{ color:'var(--ink)', fontFamily:'var(--font-mono)', fontSize:11 }}>{String(v)}</span>
                  </div>
                ))}
                <div style={{ display:'flex', gap:8, marginTop:12 }}>
                  <button className="btn btn-danger btn-sm" onClick={async () => {
                    await axios.post(`/admin/users/${selUser.user.id}/suspend`);
                    loadUsers(search, userPage); setSelUser(null);
                  }}>Suspend</button>
                  <select className="form-input" style={{ padding:'4px 8px', fontSize:12 }}
                          onChange={async e => { await axios.post(`/admin/users/${selUser.user.id}/tier`, { tier: e.target.value }); }}>
                    <option>Change tier...</option>
                    <option value="free">→ Free</option>
                    <option value="pro">→ Pro</option>
                    <option value="expert">→ Expert</option>
                  </select>
                </div>
              </div>
              <div className="card">
                <h3 style={{ fontSize:13, color:'var(--indigo)', marginBottom:10 }}>Charts ({selUser.charts?.length})</h3>
                {selUser.charts?.map((c: any) => (
                  <div key={c.id} style={{ padding:'6px 0', fontSize:12, borderBottom:'0.5px solid var(--parch2)' }}>
                    <strong>{c.name}</strong> · {String(c.dob)} · {c.place_name}
                  </div>
                ))}
                <h3 style={{ fontSize:13, color:'var(--indigo)', margin:'14px 0 8px' }}>AI Sessions ({selUser.ai_sessions?.length})</h3>
                {selUser.ai_sessions?.slice(0,5).map((s: any) => (
                  <div key={s.session_id} style={{ fontSize:11, color:'var(--ink2)', marginBottom:4 }}>
                    {s.session_id.slice(0,20)}... · {s.msg_count} msgs
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Payments ── */}
        {tab === 'payments' && (
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:'var(--indigo)', color:'#fff' }}>
                  {['Type','User','Item','Provider','Amount','Payment Ref','Discount Code','Discount %','Status','Date'].map(h => (
                    <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:11 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.map((p, i) => (
                  <tr key={p.row_key || p.id} style={{ background:i%2===0?'var(--cream)':'var(--ivory)', borderBottom:'0.5px solid var(--parch2)' }}>
                    <td style={{ padding:'7px 10px' }}>
                      <span className={`badge ${p.kind==='subscription'?'badge-saffron':'badge-teal'}`}>{p.kind==='subscription'?'Subscription':'Report'}</span>
                    </td>
                    <td style={{ padding:'7px 10px' }}>{p.email}</td>
                    <td style={{ padding:'7px 10px', color:'var(--ink2)' }}>{p.label}</td>
                    <td style={{ padding:'7px 10px', color:'var(--ink2)' }}>{p.provider}</td>
                    <td style={{ padding:'7px 10px', fontFamily:'var(--font-mono)' }}>₹{Math.round(p.amount_paise/100)}</td>
                    <td style={{ padding:'7px 10px', fontFamily:'var(--font-mono)', color:'var(--ink3)', fontSize:10 }}>{p.payment_ref}</td>
                    <td style={{ padding:'7px 10px', fontFamily:'var(--font-mono)', color:p.discount_code?'var(--teal)':'var(--ink3)' }}>{p.discount_code||'—'}</td>
                    <td style={{ padding:'7px 10px', color:p.discount_pct?'var(--teal)':'var(--ink3)' }}>{p.discount_pct?`${p.discount_pct}%`:'—'}</td>
                    <td style={{ padding:'7px 10px' }}>
                      <span className={p.status==='captured'?'badge badge-teal':'badge badge-red'}>{p.status}</span>
                    </td>
                    <td style={{ padding:'7px 10px', color:'var(--ink3)' }}>{String(p.created_at).slice(0,10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Discounts ── */}
        {tab === 'discounts' && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <h3 style={{ fontSize:14, color:'var(--indigo)' }}>Discount Codes</h3>
              <button className="btn btn-primary btn-sm"
                      onClick={() => { setShowNewDiscount(!showNewDiscount); setDiscountMsg(''); }}>
                {showNewDiscount ? 'Cancel' : '+ New Code'}
              </button>
            </div>
            {discountMsg && (
              <div className={`info-box ${discountMsg.startsWith('✓')?'info-teal':'info-red'}`} style={{ marginBottom:12 }}>{discountMsg}</div>
            )}
            {showNewDiscount && (
              <div className="card" style={{ marginBottom:16, background:'var(--sfll)', border:'1px solid var(--sfb)' }}>
                <h4 style={{ fontSize:13, color:'var(--indigo)', marginBottom:12 }}>Create New Discount Code</h4>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  {([
                    ['Code *','code','text','e.g. CIOK'],
                    ['Discount % *','discount_pct','number','e.g. 10'],
                    ['Valid From (optional)','valid_from','date',''],
                    ['Valid To (optional)','valid_to','date',''],
                    ['Max Uses (blank=unlimited)','max_uses','number','e.g. 20'],
                    ['Restrict to emails (comma sep)','allowed_emails','text','user1@gmail.com'],
                  ] as [string,string,string,string][]).map(([label,key,type,ph]) => (
                    <div key={key}>
                      <label style={{ fontSize:11, color:'var(--ink3)', display:'block', marginBottom:4 }}>{label}</label>
                      <input className="form-input" type={type} placeholder={ph}
                             value={(newDiscount as any)[key]}
                             onChange={e => setNewDiscount({...newDiscount, [key]: e.target.value})}
                             style={key==='code'?{textTransform:'uppercase',letterSpacing:1}:{}}/>
                    </div>
                  ))}
                </div>
                <button className="btn btn-primary" style={{ marginTop:14 }} onClick={handleCreateDiscount}>Create Code</button>
              </div>
            )}
            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ background:'var(--indigo)', color:'#fff' }}>
                    {['Code','Discount','Valid From','Valid To','Uses','Restricted To','Status','Action'].map(h => (
                      <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:11 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {discounts.map((d, i) => (
                    <tr key={d.id} style={{ background:i%2===0?'var(--cream)':'var(--ivory)', borderBottom:'0.5px solid var(--parch2)', opacity:d.is_active?1:0.5 }}>
                      <td style={{ padding:'7px 10px', fontFamily:'var(--font-mono)', fontWeight:500, color:'var(--indigo)' }}>{d.code}</td>
                      <td style={{ padding:'7px 10px', color:'var(--sf)', fontWeight:500 }}>{d.discount_pct}%</td>
                      <td style={{ padding:'7px 10px', color:'var(--ink3)' }}>{d.valid_from||'—'}</td>
                      <td style={{ padding:'7px 10px', color:'var(--ink3)' }}>{d.valid_to||'—'}</td>
                      <td style={{ padding:'7px 10px', fontFamily:'var(--font-mono)' }}>{d.max_uses?`${d.used_count}/${d.max_uses}`:`${d.used_count}/∞`}</td>
                      <td style={{ padding:'7px 10px', color:'var(--ink3)', fontSize:11, maxWidth:160, overflow:'hidden', textOverflow:'ellipsis' }}>{d.allowed_emails||'Anyone'}</td>
                      <td style={{ padding:'7px 10px' }}><span className={d.is_active?'badge badge-teal':'badge badge-indigo'}>{d.is_active?'Active':'Disabled'}</span></td>
                      <td style={{ padding:'7px 10px' }}><button className="btn btn-ghost btn-sm" onClick={() => handleToggleDiscount(d.id)}>{d.is_active?'Disable':'Enable'}</button></td>
                    </tr>
                  ))}
                  {discounts.length===0 && (
                    <tr><td colSpan={8} style={{ padding:'20px', textAlign:'center', color:'var(--ink3)' }}>No discount codes yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Activity Log ── */}
        {tab === 'activitylog' && (
          <div>
            <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
              <input className="form-input" style={{ maxWidth:200 }}
                     placeholder="Search email/name"
                     value={activityFilter.q}
                     onChange={e => setActivityFilter({...activityFilter, q: e.target.value})}
                     onKeyDown={e => e.key==='Enter' && loadActivityLog(1)}/>
              <select className="form-input" style={{ maxWidth:180 }}
                      value={activityFilter.action}
                      onChange={e => setActivityFilter({...activityFilter, action: e.target.value})}>
                <option value="">All actions</option>
                <option value="login">login</option>
                <option value="chart_created">chart_created</option>
                <option value="ai_question_asked">ai_question_asked</option>
                <option value="payment_verified">payment_verified</option>
                <option value="discount_validated">discount_validated</option>
                <option value="forecast_generated">forecast_generated</option>
                <option value="prediction_generated">prediction_generated</option>
                <option value="muhurta_calculated">muhurta_calculated</option>
                <option value="pdf_exported">pdf_exported</option>
              </select>
              <select className="form-input" style={{ maxWidth:130 }}
                      value={activityFilter.status}
                      onChange={e => setActivityFilter({...activityFilter, status: e.target.value})}>
                <option value="">All statuses</option>
                <option value="success">success</option>
                <option value="error">error</option>
                <option value="upgrade_required">upgrade_required</option>
                <option value="quota_exceeded">quota_exceeded</option>
              </select>
              <button className="btn btn-primary btn-sm" onClick={() => loadActivityLog(1)}>Filter</button>
              <span style={{ fontSize:12, color:'var(--ink3)', alignSelf:'center' }}>{activityTotal} total</span>
              <button className="btn btn-ghost btn-sm" style={{ marginLeft:'auto' }}
                      onClick={async () => {
                        try {
                          const { action, status, q } = activityFilter;
                          const params: Record<string, string> = {};
                          if (action) params.action = action;
                          if (status) params.status = status;
                          if (q) params.q = q;
                          const resp = await axios.get('/admin/activity-log/export', { params, responseType: 'blob' });
                          const url = URL.createObjectURL(resp.data);
                          const a   = document.createElement('a');
                          a.href     = url;
                          a.download = `jyotish_activity_${new Date().toISOString().slice(0,10)}.csv`;
                          a.click();
                          URL.revokeObjectURL(url);
                        } catch { alert('Export failed. Please try again.'); }
                      }}>
                ↓ Export CSV
              </button>
            </div>
            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11.5 }}>
                <thead>
                  <tr style={{ background:'var(--indigo)', color:'#fff' }}>
                    {['Time','User','Action','Feature','Detail','Status'].map(h => (
                      <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontSize:11 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activityLog.map((a, i) => (
                    <tr key={a.id} style={{ background:i%2===0?'var(--cream)':'var(--ivory)', borderBottom:'0.5px solid var(--parch2)' }}>
                      <td style={{ padding:'6px 10px', color:'var(--ink3)', fontSize:11, whiteSpace:'nowrap' }}>{String(a.created_at).slice(0,19)}</td>
                      <td style={{ padding:'6px 10px', fontSize:11 }}>
                        <div style={{ fontWeight:500 }}>{a.user_name||a.user_email||'—'}</div>
                        <div style={{ color:'var(--ink3)', fontSize:10 }}>{a.tier}</div>
                      </td>
                      <td style={{ padding:'6px 10px', fontFamily:'var(--font-mono)', fontSize:11, color:'var(--sf)' }}>{a.action}</td>
                      <td style={{ padding:'6px 10px', color:'var(--ink3)', fontSize:11 }}>{a.feature||'—'}</td>
                      <td style={{ padding:'6px 10px', color:'var(--ink2)', fontSize:11, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis' }}>{a.input_summary||'—'}</td>
                      <td style={{ padding:'6px 10px' }}>
                        <span className={`badge ${a.status==='success'?'badge-teal':a.status==='error'?'badge-red':'badge-indigo'}`} style={{ fontSize:10 }}>{a.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {activityTotal > 50 && (
              <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:12 }}>
                {activityPage > 1 && (
                  <button className="btn btn-ghost btn-sm" onClick={() => loadActivityLog(activityPage-1)}>← Prev</button>
                )}
                <span style={{ fontSize:12, color:'var(--ink3)', alignSelf:'center' }}>
                  Page {activityPage} of {Math.ceil(activityTotal/50)}
                </span>
                {activityPage < Math.ceil(activityTotal/50) && (
                  <button className="btn btn-ghost btn-sm" onClick={() => loadActivityLog(activityPage+1)}>Next →</button>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Audit ── */}
        {tab === 'audit' && (
          <div className="card" style={{ padding:0, overflow:'hidden', maxHeight:'70vh', overflowY:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11.5 }}>
              <thead style={{ position:'sticky', top:0 }}>
                <tr style={{ background:'var(--indigo)', color:'#fff' }}>
                  {['Admin','Action','Target','IP','Time'].map(h => (
                    <th key={h} style={{ padding:'8px 10px', textAlign:'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {audit.map((a, i) => (
                  <tr key={a.id} style={{ background:i%2===0?'var(--cream)':'var(--ivory)', borderBottom:'0.5px solid var(--parch2)' }}>
                    <td style={{ padding:'6px 10px' }}>{a.admin_email}</td>
                    <td style={{ padding:'6px 10px', fontFamily:'var(--font-mono)', fontSize:11, color:'var(--sf)' }}>{a.action}</td>
                    <td style={{ padding:'6px 10px', color:'var(--ink2)' }}>{a.target_type} #{a.target_id}</td>
                    <td style={{ padding:'6px 10px', color:'var(--ink3)' }}>{a.ip_address}</td>
                    <td style={{ padding:'6px 10px', color:'var(--ink3)' }}>{String(a.created_at).slice(0,19)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Health ── */}
        {tab === 'health' && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <div>
                <h3 style={{ fontSize:14, color:'var(--indigo)', marginBottom:4 }}>System Health</h3>
                <p style={{ fontSize:11, color:'var(--ink3)' }}>
                  {health?.last_checked ? `Last checked: ${String(health.last_checked).slice(0,16).replace('T',' ')} UTC · Runs hourly` : 'Click Refresh to run checks'}
                </p>
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                {health && (
                  <div style={{ display:'flex', gap:6 }}>
                    <span style={{ fontSize:12, padding:'2px 10px', borderRadius:99, background:'#e8f5e9', color:'#2e7d32', fontWeight:500 }}>✓ {health.ok_count} OK</span>
                    {health.warn_count > 0 && <span style={{ fontSize:12, padding:'2px 10px', borderRadius:99, background:'#fff8e1', color:'#b8860b', fontWeight:500 }}>⚠ {health.warn_count} Warn</span>}
                    {health.fail_count > 0 && <span style={{ fontSize:12, padding:'2px 10px', borderRadius:99, background:'#ffebee', color:'#c62828', fontWeight:500 }}>✗ {health.fail_count} Fail</span>}
                  </div>
                )}
                <button className="btn btn-sm btn-ghost" disabled={healthLoading} onClick={() => loadHealth(true)}>
                  {healthLoading ? '⟳ Checking...' : '🔄 Refresh now'}
                </button>
              </div>
            </div>
            {healthLoading && !health && (
              <div style={{ textAlign:'center', padding:40, color:'var(--ink3)' }}>Running health checks...</div>
            )}
            {health?.checks && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:20 }}>
                {health.checks.map((c: any) => (
                  <div key={c.check_name} style={{
                    display:'flex', alignItems:'flex-start', gap:10, padding:'10px 14px', borderRadius:8,
                    background: c.status==='ok'?'#f1f8f1':c.status==='warn'?'#fffde7':'#ffebee',
                    border: `1px solid ${c.status==='ok'?'#c8e6c9':c.status==='warn'?'#fff176':'#ffcdd2'}`,
                  }}>
                    <div style={{ width:10, height:10, borderRadius:'50%', marginTop:3, flexShrink:0,
                      background: c.status==='ok'?'#2e7d32':c.status==='warn'?'#f9a825':'#c62828' }}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', justifyContent:'space-between' }}>
                        <span style={{ fontSize:12, fontWeight:500,
                          color: c.status==='ok'?'#1b5e20':c.status==='warn'?'#856404':'#7f1d1d' }}>
                          {c.check_name.replace(/_/g,' ').replace(/\w/g,(x: string)=>x.toUpperCase())}
                        </span>
                        <span style={{ fontSize:10, fontFamily:'var(--font-mono)', color:'var(--ink3)' }}>{c.duration_ms}ms</span>
                      </div>
                      <div style={{ fontSize:11, color:'var(--ink2)', marginTop:2 }}>{c.value}</div>
                      <div style={{ fontSize:10, color:'var(--ink3)', marginTop:1 }}>{c.message}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {health?.history && health.history.length > 0 && (
              <div className="card">
                <div style={{ fontSize:12, fontWeight:500, marginBottom:10, color:'var(--indigo)' }}>Last 24 Hours</div>
                <div style={{ display:'flex', gap:3, flexWrap:'wrap' }}>
                  {(() => {
                    const byHour: Record<string,string[]> = {};
                    health.history.forEach((h:any) => {
                      const hr = String(h.checked_at).slice(0,13);
                      if (!byHour[hr]) byHour[hr] = [];
                      byHour[hr].push(h.status);
                    });
                    return Object.entries(byHour).slice(0,24).map(([hr,sts]:any) => {
                      const s = sts.includes('fail')?'fail':sts.includes('warn')?'warn':'ok';
                      return <div key={hr} title={`${hr}:00 — ${s}`} style={{ width:18, height:18, borderRadius:3,
                        background: s==='ok'?'#2e7d32':s==='warn'?'#f9a825':'#c62828' }}/>;
                    });
                  })()}
                </div>
                <div style={{ fontSize:10, color:'var(--ink3)', marginTop:8 }}>Each block = 1 hour · Green=OK · Amber=Warning · Red=Fail</div>
              </div>
            )}
            {!health && !healthLoading && (
              <div style={{ textAlign:'center', padding:40, color:'var(--ink3)' }}>No health data yet. Click Refresh now to run checks.</div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
